
import { supabase } from "@/integrations/supabase/client";
import { isDebugEnabled } from "@/utils/debug";
import type { TablesInsert, Json } from "@/integrations/supabase/types";

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  level: LogLevel;
  component: string;
  message: string;
  details?: Record<string, any>;
  user_id?: string | null;
  session_id?: string | null;
  route?: string;
}

class LoggingService {
  private static instance: LoggingService;
  private userId: string | null = null;
  private sessionId: string | null = null;
  private currentRoute: string = '';
  private suppressUntil: number = 0;
  
  private constructor() {
    this.initializeLogger();
  }

  static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  private async initializeLogger() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      this.userId = session?.user?.id || null;
      this.sessionId = crypto.randomUUID(); // Generate a unique session ID
      
      // Listen for auth state changes
      supabase.auth.onAuthStateChange((event, session) => {
        this.userId = session?.user?.id || null;
        if (event === 'SIGNED_OUT') {
          this.suppressUntil = Date.now() + 2500; // grace period to avoid 401s on logout
        }
      });

      // Listen for route changes
      if (typeof window !== 'undefined') {
        this.currentRoute = window.location.pathname;
        window.addEventListener('popstate', () => {
          this.currentRoute = window.location.pathname;
        });
      }
    } catch (error) {
      console.error('Failed to initialize logger:', error);
    }
  }

  private async persistLog(entry: LogEntry): Promise<void> {
    try {
      if (typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine === false) {
        this.suppressFor(60_000);
        return;
      }
      // Cooldown after sign-out to avoid 401 spam
      if (Date.now() < this.suppressUntil) {
        return;
      }

      // Check if user is authenticated before attempting to write to database
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        // Skip logging when user is not authenticated to prevent 401 errors
        return;
      }

      // Prepare typed payload for system_logs insert
      const payload: TablesInsert<'system_logs'> = {
        level: entry.level,
        component: entry.component,
        message: entry.message,
        details: (entry.details as unknown as Json) ?? undefined,
        user_id: entry.user_id ?? undefined,
        session_id: entry.session_id ?? undefined,
        route: entry.route
      };

      const { error } = await supabase
        .from('system_logs')
        .insert(payload);

      if (error) {
        // Silent handling for network/auth/permission issues - don't spam console
        if (
          this.isNetworkError(error) ||
          error?.code === '42501' ||
          /unauthorized|invalid jwt|jwt expired|permission denied/i.test(error?.message || '')
        ) {
          // Backoff on transient network issues
          if (this.isNetworkError(error)) this.suppressFor(60_000);
          return;
        }
        console.error('Failed to persist log:', error);
      }
    } catch (error: any) {
      // Silent handling for network/auth/permission issues
      if (
        this.isNetworkError(error) ||
        error?.code === '42501' ||
        /unauthorized|invalid jwt|jwt expired|permission denied/i.test(error?.message || '')
      ) {
        if (this.isNetworkError(error)) this.suppressFor(60_000);
        return;
      }
      console.error('Error persisting log:', error);
    }
  }

  private isNetworkError(error: any): boolean {
    const raw = error?.message || String(error || '');
    const message = raw.toString().toLowerCase();
    const status = error?.status;
    return message.includes('err_internet_disconnected') ||
           message.includes('err_network_changed') ||
           message.includes('err_connection_refused') ||
           message.includes('err_connection_closed') ||
           message.includes('err_timed_out') ||
           message.includes('failed to fetch') ||
           message.includes('fetch failed') ||
           message.includes('networkerror') ||
           status === 0;
  }

  private createLogEntry(
    level: LogLevel,
    component: string,
    message: string,
    details?: Record<string, any>
  ): LogEntry {
    return {
      level,
      component,
      message,
      details: details ? JSON.parse(JSON.stringify(details)) : undefined,
      user_id: this.userId,
      session_id: this.sessionId,
      route: this.currentRoute
    };
  }

  private async log(
    level: LogLevel,
    component: string,
    message: string,
    details?: Record<string, any>
  ): Promise<void> {
    // Environment-agnostic gating: suppress info/debug unless debug is enabled
    if ((level === 'info' || level === 'debug') && !isDebugEnabled()) {
      return;
    }
    const entry = this.createLogEntry(level, component, message, details);
    
    // Log to console with appropriate styling
    const consoleStyles = {
      debug: 'color: #6B7280',
      info: 'color: #2563EB',
      warn: 'color: #D97706',
      error: 'color: #DC2626; font-weight: bold'
    };
    
    console.log(
      `%c[${new Date().toISOString()}] [${level.toUpperCase()}] [${component}]: ${message}`,
      consoleStyles[level]
    );
    
    if (details) {
      console.log(details);
    }

    // Persist to database
    return this.persistLog(entry);
  }

  public debug(component: string, message: string, details?: Record<string, any>): Promise<void> {
    return this.log('debug', component, message, details);
  }

  public info(component: string, message: string, details?: Record<string, any>): Promise<void> {
    return this.log('info', component, message, details);
  }

  public warn(component: string, message: string, details?: Record<string, any>): Promise<void> {
    return this.log('warn', component, message, details);
  }

  public error(component: string, message: string, details?: Record<string, any>): Promise<void> {
    return this.log('error', component, message, details);
  }

  // Utility method to update current route
  public setCurrentRoute(route: string): void {
    this.currentRoute = route;
  }

  // Suppress DB persistence for a duration (milliseconds)
  public suppressFor(ms: number): void {
    this.suppressUntil = Math.max(this.suppressUntil, Date.now() + ms);
  }
}

export const logger = LoggingService.getInstance();
